import { fetchDocuments } from './fetchDocuments';
import { PromiseClient } from 'hub-http/adapters/promiseClient';
import { renderDocument } from './renderDocuments';
import { fromJS } from 'immutable';
import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';

import {
  searchEntriesRpc,
  listEntriesRpc,
} from '../../../../__generated__/chirp/com/hubspot/sales/documents/rpc/SalesDocumentIndexRpc';
import { mapDocumentEntry } from './mapDocumentEntry';

export const documentsApiWrapper = (
  httpClient: PromiseClient,
  contactEmail: string
) => ({
  fetchRenderedContent({ id }: { id: number }) {
    return renderDocument({
      httpClient,
      contentId: id,
      contactEmail,
    }).then((linkInfo) => {
      const { url, text } = linkInfo;
      return {
        htmlBody: `<a href="${url}">${text}</a>`,
      };
    });
  },
  searchArticles({
    query,
    count,
  }: {
    query: string | undefined;
    count: number | undefined;
  }) {
    return fetchDocuments({
      httpClient,
      queryParams: {
        query,
      },
    }).then((results: any) => {
      const finalResults = count ? results.slice(0, count) : results;
      return finalResults.map((item: any) => {
        return {
          ...item,
          value: item.id,
        };
      });
    });
  },
});

export type Document = {
  active: true;
  createdAt: number;
  folderId: null;
  id: number;
  isProcessingSlides: false;
  originalS3Link: string;
  pdfLink: string;
  portalId: number;
  slideIds: number[];
  slides: unknown[];
  title: string;
  updatedAt: number;
  userId: number;
};

export type DocumentSearchResultType = {
  contentId: number;
  name: string;
  documentId: string;
  brandId?: number;
  metadata: {
    title: string;
    thumbnailUrl: string;
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
};

export type DocumentSearchQueryParams = {
  limit?: number;
  offset?: number;
  query?: string;
};

export type Auth = {
  user: {
    user_id: number;
  };
};

export type DocumentSearchResponse = {
  total: number;
  offset: number;
  results: DocumentSearchResultType[];
};

// TODO(SalesDocumentsCHIRPMigration cleanup): delete along with `searchDocuments`,
// `fetchDocuments`, and `normalizeRestDocument`. Only the REST path carries the
// JSON-encoded `description`; CHIRP delivers `brandId` first-class.
type RestDocumentResult = Omit<DocumentSearchResultType, 'brandId'> & {
  description?: string | null;
};

export type RestDocumentResponse = {
  total: number;
  offset: number;
  results: RestDocumentResult[];
};

export const normalizeRestDocument = (
  r: RestDocumentResult
): DocumentSearchResultType => {
  const { description, ...rest } = r;
  let brandId: number | undefined;
  if (description) {
    try {
      const parsed = JSON.parse(description);
      if (parsed?.brandId != null) brandId = Number(parsed.brandId);
    } catch {
      // malformed description — treat as no brand
    }
  }
  return { ...rest, brandId };
};

export function fetchDocument(id: number) {
  return http.get<Document>(`presentations/v1/deck/${id}`).then(fromJS);
}

export function createDocumentLink(
  documentId: number,
  skipForm: boolean,
  sharedWith: string
) {
  return http
    .post(`presentations/v1/deck/${documentId}/share`, {
      query: {
        isLive: true,
        skipForm,
        sharedWith,
      },
    })
    .then(fromJS);
}

export function searchDocuments(
  searchInput: string
): Promise<DocumentSearchResponse> {
  return http
    .post<RestDocumentResponse>('/salescontentsearch/v2/search', {
      data: {
        contentTypesToHydrate: ['DOCUMENT', 'DOCUMENTS_FOLDER'],
        filters: [{ field: 'content_type', values: ['DOCUMENT'] }],
        limit: 100,
        offset: 0,
        query: searchInput,
      },
    })
    .then((res) => ({
      ...res,
      results: res.results.map(normalizeRestDocument),
    }));
}

const CHIRP_SEARCH_LIMIT = 100;

export async function searchDocumentsCHIRP(
  searchInput: string
): Promise<DocumentSearchResponse> {
  const rpcClient = createRpcClientV2({ hubHttpClient: http });
  const hasQuery = searchInput.length > 0;
  const response = hasQuery
    ? await rpcClient.call(searchEntriesRpc, {
        request: {
          query: searchInput,
          entryTypes: ['DOCUMENT'],
          filters: [],
          folderScope: 'ALL',
          pageLength: CHIRP_SEARCH_LIMIT,
          offset: 0,
        },
      })
    : await rpcClient.call(listEntriesRpc, {
        request: {
          entryTypes: ['DOCUMENT'],
          filters: [],
          folderScope: 'ALL',
          pageLength: CHIRP_SEARCH_LIMIT,
          offset: 0,
        },
      });
  const results = (response.results ?? [])
    .map(mapDocumentEntry)
    .filter(
      (
        item: DocumentSearchResultType | null
      ): item is DocumentSearchResultType => item !== null
    );
  return {
    total: results.length,
    offset: response.offset,
    results,
  };
}
